import { useCallback, useEffect, useState } from "react";
import { Zap, Check, X, Undo2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const STATUS_META = {
  nieuw: { label: "nieuw", cls: "bg-amber-100 text-amber-700" },
  goedgekeurd: { label: "goedgekeurd", cls: "bg-emerald-100 text-emerald-700" },
  afgewezen: { label: "afgewezen", cls: "bg-red-100 text-red-600" },
};

export const ChargerReportsAdmin = () => {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/charger-reports").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (item, status) => {
    try {
      await api.patch(`/admin/charger-reports/${item.id}`, { status });
      toast.success(status === "goedgekeurd"
        ? `"${item.name}" toegevoegd — vanaf nu vindbaar voor alle reizigers`
        : `Melding → ${STATUS_META[status].label}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const nieuw = items.filter((i) => i.status === "nieuw").length;

  return (
    <div data-testid="admin-charger-reports-section" className="mb-10">
      <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-1">
        <Zap className="w-5 h-5 text-primary" /> Laadpunt-meldingen ({items.length}){nieuw > 0 && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{nieuw} nieuw</span>}
      </h2>
      <p className="text-xs text-muted-foreground mb-4">Handmatig toegevoegde laadpunten van reizigers. Goedkeuren maakt ze vindbaar in alle zoekresultaten.</p>
      <div className="space-y-2" data-testid="admin-charger-reports-list">
        {items.length === 0 && <p className="text-sm text-muted-foreground bg-card rounded-2xl p-5 text-center">Nog geen meldingen van ontbrekende laadpunten.</p>}
        {items.map((it) => {
          const st = STATUS_META[it.status] || STATUS_META.nieuw;
          return (
            <div key={it.id} data-testid={`charger-report-row-${it.id}`} className="bg-card rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm flex items-center gap-2 min-w-0">
                  <span className="truncate">{it.name}</span>
                  {(it.operator === "Tesla" || /supercharger/i.test(it.name || "")) && <span className="shrink-0 text-[9px] font-bold bg-red-500/10 text-red-600 rounded-full px-1.5 py-0.5">TESLA</span>}
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {it.max_power_kw ? `${it.max_power_kw} kW · ` : ""}{it.town ? `${it.town} · ` : ""}gemeld door {it.user_name || it.user_email} · {it.created_at?.slice(0, 10)}
                  {" · "}
                  <a href={`https://www.google.com/maps?q=${it.lat},${it.lon}`} target="_blank" rel="noreferrer"
                    data-testid={`charger-report-map-${it.id}`} className="text-primary font-semibold inline-flex items-center gap-0.5">
                    kaart <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {it.status !== "goedgekeurd" && (
                  <button data-testid={`charger-report-approve-${it.id}`} onClick={() => setStatus(it, "goedgekeurd")}
                    className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-xs font-bold px-3.5 py-1.5 inline-flex items-center gap-1 transition-[background-color] duration-200">
                    <Check className="w-3.5 h-3.5" /> Goedkeuren
                  </button>
                )}
                {it.status === "nieuw" && (
                  <button data-testid={`charger-report-reject-${it.id}`} onClick={() => setStatus(it, "afgewezen")}
                    className="rounded-full bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold px-3.5 py-1.5 inline-flex items-center gap-1 transition-[background-color] duration-200">
                    <X className="w-3.5 h-3.5" /> Afwijzen
                  </button>
                )}
                {it.status !== "nieuw" && (
                  <button data-testid={`charger-report-undo-${it.id}`} onClick={() => setStatus(it, "nieuw")}
                    title="Terug naar 'nieuw' (goedgekeurde laadpunten worden weer verwijderd uit de zoekresultaten)"
                    className="rounded-full bg-secondary hover:bg-border text-xs font-bold px-3.5 py-1.5 inline-flex items-center gap-1 transition-[background-color] duration-200">
                    <Undo2 className="w-3.5 h-3.5" /> Ongedaan
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
